import type { InferOutput } from 'valibot'
import { array, intersect, literal, number, object, optional, picklist, pipe, record, string, transform, union } from 'valibot'
import { transformFacetField } from '#shared/utils/solr-facet'

/**
 * List of valid Solr facet fields
 * Includes both standard and excluded versions for multi-select faceting
 *
 * @see https://solr.apache.org/guide/solr/latest/query-guide/faceting.html#tagging-and-excluding-filters
 */
export const facetFieldsList = picklist([
  'author_list',
  `{!ex=${FILTER_FACET_TAGS.author}}author_list`,
  'legal_areas',
  `{!ex=${FILTER_FACET_TAGS.legalArea}}legal_areas`,
  'nodeType',
  `{!ex=${FILTER_FACET_TAGS.nodeType}}nodeType`,
  'author',
])

export type FacetFieldsList = InferOutput<typeof facetFieldsList>

const baseFacetSchema = object({
  type: literal('terms'),
  field: facetFieldsList,
  limit: number(),
  mincount: optional(number()),
  sort: optional(picklist(['count desc', 'index asc'])),
})

export const termsFacetSchema = intersect([
  baseFacetSchema,
  object({
    facet: optional(record(string(), baseFacetSchema)),
  }),
])

export const facetQuerySchema = record(string(), termsFacetSchema)

export type TermsFacet = InferOutput<typeof termsFacetSchema>
export type FacetQuery = InferOutput<typeof facetQuerySchema>

/**
 * Schema for individual facet field values with transformation
 * Transforms Solr's alternating array format into key-value objects
 */
export const facetFieldSchema = pipe(
  array(union([string(), number()])),
  transform(transformFacetField),
  array(object({
    value: string(),
    count: number(),
  })),
)

/**
 * Schema for facet fields (field names mapped to transformed value/count objects)
 */
export const facetFieldsSchema = record(string(), facetFieldSchema)

/**
 * Simplified facet_counts schema with only facet_fields
 */
export const facetCountsSchema = object({
  facet_fields: facetFieldsSchema,
})

export type FacetField = InferOutput<typeof facetFieldSchema>
export type FacetFields = InferOutput<typeof facetFieldsSchema>
export type FacetCounts = InferOutput<typeof facetCountsSchema>

/**
 * Schema for author bucket with nested legal_areas
 */
export const jsonFacetAuthorWithLegalAreasSchema = object({
  val: string(),
  count: number(),
  legal_areas: object({
    buckets: array(object({
      val: string(),
      count: number(),
    })),
  }),
})

/**
 * Schema for complete Solr JSON faceting response
 * Used when fetching author → legal_areas mapping
 * Supports both author_list (commentaries/articles) and author (books) fields
 */
export const jsonFacetAuthorsResponseSchema = object({
  facets: object({
    count: number(),
    authors_list: object({
      buckets: array(jsonFacetAuthorWithLegalAreasSchema),
    }),
    author: object({
      buckets: array(jsonFacetAuthorWithLegalAreasSchema),
    }),
  }),
})

export type JsonFacetAuthorsResponse = InferOutput<typeof jsonFacetAuthorsResponseSchema>

/**
 * Schema for a single JSON Facet bucket list
 */
export const jsonFacetBucketsSchema = object({
  buckets: array(object({
    val: string(),
    count: number(),
  })),
})

/**
 * Schema for the search endpoint's JSON Facet response (post-collapse counts)
 */
export const jsonFacetSearchResponseSchema = object({
  count: number(),
  nodeType: optional(jsonFacetBucketsSchema),
  author_list: optional(jsonFacetBucketsSchema),
  legal_areas: optional(jsonFacetBucketsSchema),
})

export type JsonFacetBuckets = InferOutput<typeof jsonFacetBucketsSchema>
export type JsonFacetSearchResponse = InferOutput<typeof jsonFacetSearchResponseSchema>
